<template>
  <div class="m-col" :style="colStyle" :class="colClasses">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  cols?: number
  flex?: string
  /** Swing td width: content = hug; grow = fill remainder next to content */
  widthMode?: 'content' | 'grow'
  padding?: string
  columnIndex?: number
  rowspan?: number
  isMediawall?: boolean
  /** По умолчанию auto — у узких ячеек с меткой+кнопками может появляться горизонтальный скролл */
  overflow?: 'auto' | 'visible' | 'hidden'
}

const props = withDefaults(defineProps<Props>(), {
  cols: 100,
  flex: undefined,
  widthMode: undefined,
  padding: '10px 6px 0 6px',
  columnIndex: undefined,
  isMediawall: false,
  overflow: undefined,
})

const colStyle = computed(() => {
  const baseCols = Math.min(100, Math.max(0, props.cols))
  const width = baseCols >= 100 ? '100%' : `${baseCols}%`

  const baseStyle: Record<string, string> = {
    // display:flex — в .m-col (CSS), не inline: иначе v-show на MCol не скрывает ячейку
    padding: props.padding,
    minHeight: '0',
    minWidth: '0',
    boxSizing: 'border-box',
  }

  if (props.flex) {
    baseStyle.flex = props.flex
    baseStyle.maxWidth = width
  } else if (props.widthMode === 'content') {
    // Swing weight 0: preferred/hug size, do not stretch
    baseStyle.flex = '0 0 auto'
    baseStyle.width = 'auto'
    baseStyle.maxWidth = 'none'
  } else if (props.widthMode === 'grow') {
    // Fill remainder beside content-width siblings
    const grow = Math.max(1, baseCols || 1)
    baseStyle.flex = `${grow} 1 0%`
    baseStyle.width = 'auto'
    baseStyle.maxWidth = 'none'
  } else {
    // Shrink allowed so row children fit; basis is preferred share of the row.
    baseStyle.flex = `0 1 ${width}`
    baseStyle.maxWidth = width
    baseStyle.width = width
  }

  // MediaWall elements need different layout behavior
  if (props.isMediawall) {
    baseStyle.flexDirection = 'column'
    baseStyle.height = '100%'
    baseStyle.overflow = 'visible'
  } else {
    baseStyle.overflow = props.overflow ?? 'auto'
  }

  return baseStyle
})

const colClasses = computed(() => ({
  'm-col--constrained': true,
}))
</script>

<style scoped>
.m-col {
  box-sizing: border-box;
  display: flex;
}

@media (max-width: 768px) {
  .m-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
}
</style>
